import React, { useState, useEffect } from 'react';
import {
  SimpleTable,
  SimpleTableContainer,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableData,
  SimpleTableWrapper
} from 'cdk-radial';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import { FormattedMessage } from 'react-intl';
import FortellisConstants from '../common/FortellisConstants';
import { withAuth } from '@cdk-prod/fortellis-auth-context';
import SolutionListingAPI from '../../api/StoreListingAPI';
import { environmentURLs } from '../common/environment/CaptureEnvironment';
import replaceAll from 'string.prototype.replaceall';
import { sendAmplitudeData } from '../../utils/amplitude';
import {
  AMPLITUDE_EVENTS,
  AMPLITUDE_PROPERTIES
} from '../../utils/amplitude-constants';
import Spinner from '../common/Spinner';
import SolutionStatus from '../common/SolutionStatus';

const ApiInfoSimpleTableRow = styled(SimpleTableRow)`
  :nth-child(even) {
    background-color: #151b250d;
    border-top: 1px solid #dcddde;
  }
  &:last-child {
    border-bottom: none;
  }
`;

const getAsyncApiData = gql`
  query($id: ID) {
    asyncApiDetails(id: $id) {
      id
      orgId
      name
      status
      website
      created
      apigeeAppId
      displayName
      description
      oneToOneUI
      apiProducts {
        name
        displayName
      }
      integrations {
        id
        name
        displayName
        description
        apiType
        providers {
          id
          apiProviderName
          entityId
        }
      }
      allIntegrations {
        id
        name
        displayName
        description
        apiType
        providers {
          id
          apiProviderName
          entityId
        }
      }
    }
  }
`;

const removeApiV2Prefix = str => {
  const prefixToRemove = 'api-v2-';
  return str.startsWith(prefixToRemove)
    ? str.substring(prefixToRemove.length)
    : str;
};

const APIDescriptionTextLimit = 150;
export const ReadMore = ({ textLimit, text }) => {
  const [showMore, setShowMore] = useState(false);
  const [desc, setDesc] = useState(text);

  useEffect(() => {
    if (text.length > textLimit) {
      if (showMore) {
        setDesc(text);
      } else {
        setDesc(text.substring(0, textLimit).trim() + '...');
      }
    }
  }, [showMore]);

  function suppressMarkdown(desc = '') {
    return replaceAll(desc, '#', '');
  }
  const apiDescription = () => {
    return suppressMarkdown(desc);
  };

  return (
    <>
      <div className="api_description">{apiDescription()}</div>
    </>
  );
};
class ApiInfo extends React.Component {
  state = {
    apiProviderMapping: null,
    loading: false,
    isConsentLoaded: false,
    providersData: [],
    apisLoaded: false
  };

  getApis(solution) {
    if (this.isFortellisSolution()) {
      return solution.registration_details &&
        solution.registration_details.api_details
        ? solution.registration_details.api_details.sort((a, b) => {
            var x =
              a && a.displayName ? a.displayName.toLowerCase() : undefined;
            var y =
              b && b.displayName ? b.displayName.toLowerCase() : undefined;
            if (x > y) return 1;
            if (x < y) return -1;
            return 0;
          })
        : [];
    }
    if (this.isPartnerProgram()) {
      return solution.partner_program_registration_details &&
        solution.partner_program_registration_details.partner_api_details
        ? solution.partner_program_registration_details.partner_api_details.sort(
            (a, b) => {
              var x =
                a && a.applicationName
                  ? a.applicationName.toLowerCase()
                  : undefined;
              var y =
                b && b.applicationName
                  ? b.applicationName.toLowerCase()
                  : undefined;
              if (x > y) return 1;
              if (x < y) return -1;
              return 0;
            }
          )
        : [];
    }
    return [];
  }

  getSolutionType(solution) {
    return solution.solutionType;
  }

  isPartnerProgram() {
    return (
      this.props.solution.solutionType === FortellisConstants.PARTNER_PROGRAM
    );
  }

  isFortellisSolution() {
    return this.props.solution.solutionType === FortellisConstants.FORTELLIS;
  }

  createAndSubmitAmplitudeData = api => {
    const { solution, entity } = this.props;
    let amplitudeData = {
      [AMPLITUDE_PROPERTIES.APP_ID]: solution?.id,
      [AMPLITUDE_PROPERTIES.APP_NAME]: solution?.overview?.name,
      [AMPLITUDE_PROPERTIES.ORG_ID]: entity?.id || 'N/A',
      [AMPLITUDE_PROPERTIES.ORG_NAME]: entity?.name || 'N/A',
      [AMPLITUDE_PROPERTIES.API_ID]: api?.id || 'N/A',
      [AMPLITUDE_PROPERTIES.API_NAME]: api?.displayName || 'N/A'
    };
    sendAmplitudeData(AMPLITUDE_EVENTS.VIEW_API, amplitudeData);
  };

  async getAsyncApiDetails(id) {
    let response = await this.props.client.query({
      query: getAsyncApiData,
      variables: {
        id: id
      }
    });
    return response.data;
  }

  matchDataDynamic(listingApis, allProvider) {
    if (!Array.isArray(listingApis)) {
      // If listingApis is null or not an array, return allProvider directly
      return allProvider || [];
    }

    if (!Array.isArray(allProvider)) {
      return listingApis || [];
    }

    const result = [];

    // Process all listing APIs and enrich with matching DNP provider data
    listingApis.forEach(listingApi => {
      const listingId = listingApi.id.startsWith('api-v2-')
        ? listingApi.id.substring(7)
        : listingApi.id;

      // Find matching DNP data
      const matchedDnpItem = allProvider.find(dnpItem => {
        const dnpId = dnpItem.id.startsWith('api-v2-')
          ? dnpItem.id.substring(7)
          : dnpItem.id;
        return listingId === dnpId;
      });

      if (matchedDnpItem) {
        // Match found: use listing data structure but fill providers and apiType from DNP
        result.push({
          ...listingApi,
          apiType: matchedDnpItem.apiType || listingApi.apiType,
          providers: matchedDnpItem.providers || listingApi.providers
        });
      } else {
        // No match: keep listing data as-is
        result.push(listingApi);
      }
    });

    return result;
  }

  async fetchProviders() {
    const solutionId = this.props.solution.id;
    let isSolutionEditable = true;

    // Check if solution is recalled by system
    const isSystemRecalled =
      this.props.solution.status === SolutionStatus.RECALLED &&
      this.props.solution.comments &&
      this.props.solution.comments.length > 0 &&
      this.props.solution.comments[this.props.solution.comments.length - 1]
        ?.userId === 'System';

    if (
      !(
        this.props.solution.status === SolutionStatus.DRAFT ||
        this.props.solution.status === SolutionStatus.REJECTED ||
        (this.props.solution.status === SolutionStatus.RECALLED &&
          !isSystemRecalled)
      )
    ) {
      isSolutionEditable = false;
    }
    let apiProviderResponse = await SolutionListingAPI.anonymous.getApiDetailsForSolution(
      { solutionId: solutionId }
    );
    this.setState({ apisLoaded: true });
    const filteredProviderData = this.matchDataDynamic(
      this.props?.solution?.registration_details?.api_details,
      apiProviderResponse.data.data.providers
    );

    // if solution is editable then pick from all data and active records from DNP side
    if (isSolutionEditable) {
      const editableStateData = apiProviderResponse.data.data.providers.filter(
        item => item.status !== 'retired'
      );
      this.setState({ providersData: editableStateData || [] }, () => {});
    }

    // if solution is non editable then pick listing data and match it with all data from DNP for providers
    else {
      this.setState({ providersData: filteredProviderData }, () => {});
    }
  }

  componentDidMount() {
    if (!this.props.solution || !this.props.solution.overview) {
      return null;
    }
    this.fetchProviders();
  }

  render() {
    const { solution } = this.props;
    if (!solution || !solution.overview) {
      return null;
    }
    return (
      <div>
        <div>
          <div className="apiInfo-subtitle">
            {this.isFortellisSolution() ? (
              <FormattedMessage
                id="ApiInfo.helpText"
                defaultMessage={`This app uses the following APIs published on the Fortellis platform. Each API can have one or more providers. Please check if your preferred app provider is in the list because the selection of the API provider will be required while configuring the app subscription.`}
              />
            ) : (
              <FormattedMessage
                id="ApiInfo.apiUsedByIntegrationMessage"
                defaultMessage={`This app uses external APIs which are not integrated through the Fortellis platform. This app integrates with the following applications.`}
              />
            )}
          </div>
          <div className="api-info-table">
            <SimpleTableWrapper data-testid="apiInfo-simple-table-wrapper">
              <SimpleTableContainer>
                <SimpleTable aria-label="ApiInfo-Table">
                  <SimpleTableHead>
                    <SimpleTableRow>
                      <SimpleTableHeader
                        className={
                          this.isFortellisSolution()
                            ? 'api-info-table-cell-25'
                            : 'api-info-table-cell-50'
                        }
                        colSpan="1"
                      >
                        {this.isFortellisSolution() ? (
                          <FormattedMessage
                            id="ApiInfo.apiName"
                            defaultMessage="Fortellis APIs"
                          />
                        ) : (
                          <FormattedMessage
                            id="ApiInfo.applicationiName"
                            defaultMessage="Application Name"
                          />
                        )}
                      </SimpleTableHeader>
                      <SimpleTableHeader
                        className="api-info-table-cell-25"
                        data-cy="th_api_info_description"
                        colSpan="1"
                      >
                        <FormattedMessage
                          id="ApiInfo.description"
                          defaultMessage="Description"
                        />
                      </SimpleTableHeader>
                      <SimpleTableHeader
                        className="api-info-table-cell-25"
                        colSpan="1"
                        data-cy="th_api_info_provider"
                      >
                        <FormattedMessage
                          id="ApiInfo.providers"
                          defaultMessage="Providers"
                        />
                      </SimpleTableHeader>
                    </SimpleTableRow>
                  </SimpleTableHead>
                </SimpleTable>
              </SimpleTableContainer>
            </SimpleTableWrapper>
          </div>
          {this.state?.providersData &&
            this.state?.providersData?.map((api, index) => {
              if (
                this.isFortellisSolution() &&
                !this.state?.providersData.find(
                  provider =>
                    removeApiV2Prefix(provider.id) === removeApiV2Prefix(api.id)
                )
              ) {
                return null;
              }
              return (
                <ApiInfoSimpleTableRow
                  data-testid="apiInfo-simple-table-row"
                  key={index}
                >
                  {/* api name with link */}
                  <SimpleTableData
                    className={
                      this.isFortellisSolution()
                        ? 'api-info-table-cell-25'
                        : 'api-info-table-cell-50'
                    }
                  >
                    <div className="apiInfo-table-body-api-info">
                      {this.isFortellisSolution() && this.state?.providersData
                        ? this.state.providersData.map(provider => {
                            if (
                              removeApiV2Prefix(provider.id) ===
                              removeApiV2Prefix(api.id)
                            ) {
                              return (
                                <div>
                                  <p>
                                    <a
                                      href={`${
                                        environmentURLs.api_reference_URL
                                      }${
                                        provider.apiType === 'api'
                                          ? 'apis'
                                          : 'async-apis'
                                      }/${removeApiV2Prefix(api.id)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() =>
                                        this.createAndSubmitAmplitudeData(api)
                                      }
                                    >
                                      {' '}
                                      {provider.displayName ||
                                        api.displayName}{' '}
                                      <i className="material-icons external-link-icon">
                                        open_in_new
                                      </i>{' '}
                                    </a>

                                    <br />
                                  </p>
                                </div>
                              );
                            }
                          })
                        : api.applicationName}
                    </div>
                  </SimpleTableData>
                  {/* api description */}
                  <SimpleTableData className="api-info-table-cell-25">
                    <div className="apiInfo-table-body-api-info">
                      <ReadMore
                        textLimit={APIDescriptionTextLimit}
                        text={(api && api.description) || ''}
                      />
                    </div>
                  </SimpleTableData>

                  {/* provider name */}
                  <SimpleTableData
                    className={
                      this.isFortellisSolution()
                        ? 'api-info-table-cell-25'
                        : 'api-info-table-cell-50'
                    }
                  >
                    {this.isFortellisSolution() ? (
                      <div className="apiInfo-table-body-api-info">
                        {this.state.providersData.map(provider => {
                          if (
                            removeApiV2Prefix(api.id) ===
                            removeApiV2Prefix(provider.providers[0].id)
                          )
                            return (
                              <div className="api-info-table-body-providers-item">
                                {`${provider.providers[0].name}`}
                              </div>
                            );
                        })}
                      </div>
                    ) : null}
                  </SimpleTableData>
                </ApiInfoSimpleTableRow>
              );
            })}
        </div>
        {/* just in case we are still loading the required data */}
        {!this.state?.apisLoaded && <Spinner />}
      </div>
    );
  }
}

export default injectIntl(withApollo(withAuth(ApiInfo)));
